import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Clear, Add } from '@mui/icons-material';
import { Grid, TextField, InputAdornment, IconButton } from '@mui/material';
import { DateField } from '@mui/x-date-pickers';
import { DateTime } from 'luxon';
import { useDebouncedCallback } from 'use-debounce';

import * as colors from 'commons/styles/colors';
import { STOItem, STOProcessedItem } from 'commons/types';
import {
  getSTOReceiveProductDetail,
  postSTOReceiveItem,
  postSTOReceiveRemoveBatch,
} from 'redux-stores/actions';
import { snackbarSetData } from 'redux-stores/reducers/utilityReducer';
import { AppDispatch } from 'redux-stores/store';

import S from '../STOReceiveItemPage.style';

const DEFAULT_NEW_RECEIVE_ACTUAL = {
  basket: null,
  bin: '',
  inventory_number: '',
  bin_type: '',
  expiry_date: '',
  quantity: 0,
  is_verified: false,
};

interface STOReceiveProcessFormProps {
  stoItem: STOItem;
  itemIdx: number;
}

export const STOReceivingProcessForm: React.FC<STOReceiveProcessFormProps> = ({
  stoItem,
  itemIdx,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const [currentSTOItem, setCurrentSTOItem] = useState<STOItem | null>(null);
  const [currentReceiveActual, setCurrentReceiveActual] = useState<
    STOProcessedItem[]
  >([]);

  /** ***** FUNCTIONS ***** */
  const _verifyInventoryNumber = (
    input: string,
  ): Pick<STOProcessedItem, 'inventory_number' | 'expiry_date'> | null => {
    const item = currentSTOItem?.pick_actual.find(
      (pick) => pick.inventory_number === input,
    );
    if (!item) {
      dispatch(
        snackbarSetData({
          open: true,
          color: colors.PRIMARY_RED,
          message: 'Batch number should match with pick actual data!',
        }),
      );
      return null;
    } else {
      dispatch(
        snackbarSetData({
          open: false,
        }),
      );
    }

    return {
      inventory_number: item.inventory_number,
      expiry_date: item.expiry_date,
    };
  };

  const handleAddBatch = (): void => {
    const newPickActual = structuredClone(currentReceiveActual);
    newPickActual.push(DEFAULT_NEW_RECEIVE_ACTUAL);
    setCurrentReceiveActual(newPickActual);
  };

  const handleRemoveBatch = (): void => {
    const newPickActual = structuredClone(currentReceiveActual);
    const removedSource = newPickActual.pop();

    if (removedSource && removedSource.bin && removedSource.inventory_number) {
      postSTOReceiveRemoveBatch(
        {
          sto_number: stoItem.sto_number,
          sku_code: stoItem.sku_code,
          item: removedSource,
          item_idx: itemIdx,
        },
        dispatch,
      );
    }
    setCurrentReceiveActual(newPickActual);
  };

  /** ***** DEBOUNCE FUNCTIONS ***** */
  const debouncedRemoveBatch = useDebouncedCallback(
    (params: {
      stoNumber: string;
      skuCode: string;
      item: STOProcessedItem;
    }) => {
      postSTOReceiveRemoveBatch(
        {
          sto_number: params.stoNumber,
          sku_code: params.skuCode,
          item: params.item,
          item_idx: itemIdx,
        },
        dispatch,
      );
    },
    500,
    {
      maxWait: 2000,
    },
  );
  const debouncedProductDetail = useDebouncedCallback(
    async (params: { binNumber: string; pickIndex: number }) => {
      const { pickIndex, ...restParams } = params;
      const result = await getSTOReceiveProductDetail(restParams, dispatch);
      const clonedReceived = structuredClone(currentReceiveActual);
      if (result) {
        clonedReceived[pickIndex].bin = result.name;
        clonedReceived[pickIndex].bin_type = result.bin_type;
      } else {
        clonedReceived[pickIndex].bin = '';
        clonedReceived[pickIndex].bin_type = '';
      }
      setCurrentReceiveActual(clonedReceived);
    },
    500,
    { maxWait: 2000, trailing: true },
  );
  const debouncedFocusNextField = useDebouncedCallback((selectorId: string) => {
    const nextInput = document.querySelector<HTMLInputElement>(selectorId);
    if (nextInput) nextInput.focus();
  }, 500);
  const debouncedReceiveItem = useDebouncedCallback(
    (params: {
      stoNumber: string;
      skuCode: string;
      item: STOProcessedItem;
    }) => {
      postSTOReceiveItem(
        {
          sto_number: params.stoNumber,
          sku_code: params.skuCode,
          item: params.item,
          item_idx: itemIdx,
        },
        dispatch,
      );
    },
    500,
    {
      maxWait: 2000,
    },
  );

  /** ***** USE EFFECT ***** */
  useEffect(() => {
    if (
      !currentSTOItem ||
      stoItem.sku_code !== currentSTOItem.sku_code ||
      stoItem.sto_number !== currentSTOItem.sto_number
    ) {
      const defaultReceiveActual: STOProcessedItem[] = [
        DEFAULT_NEW_RECEIVE_ACTUAL,
      ];
      setCurrentReceiveActual(
        stoItem.receive_actual.length > 0
          ? stoItem.receive_actual
          : defaultReceiveActual,
      );
      setCurrentSTOItem(stoItem);
    }
  }, [currentSTOItem, stoItem]);

  const renderForm = () =>
    currentReceiveActual.map((info, idx) => {
      const onFieldChange = (
        type: keyof STOProcessedItem,
        value: string,
        nextField?: string,
      ): void => {
        const clonedReceived = structuredClone(currentReceiveActual);
        const prevValue = clonedReceived[idx][type];
        const cleanNewValue = value.trim();

        if (
          (type === 'bin' || type === 'inventory_number') &&
          clonedReceived[idx].quantity !== 0
        ) {
          const newBatch: STOProcessedItem = {
            bin: '',
            inventory_number: '',
            bin_type: '',
            expiry_date: '',
            quantity: 0,
            basket: clonedReceived[idx].basket,
            is_verified: false,
          };
          newBatch[type] = cleanNewValue;
          const replaced = clonedReceived.splice(idx, 1, newBatch);
          if (newBatch.bin === '' || newBatch.inventory_number === '') {
            debouncedRemoveBatch({
              stoNumber: stoItem.sto_number,
              skuCode: stoItem.sku_code,
              item: replaced[0],
            });
          }
        } else if (type === 'bin') {
          clonedReceived[idx][type] = cleanNewValue;
          if (cleanNewValue === '') {
            clonedReceived[idx].bin_type = '';
          }
        } else if (type === 'inventory_number') {
          clonedReceived[idx][type] = cleanNewValue;
          const sourceInfo = _verifyInventoryNumber(cleanNewValue);
          if (sourceInfo) {
            clonedReceived[idx].expiry_date = sourceInfo.expiry_date;
          } else {
            clonedReceived[idx].expiry_date = '';
          }
        } else if (
          type === 'quantity' &&
          cleanNewValue !== '' &&
          !Number.isNaN(+cleanNewValue)
        ) {
          // to determine how many quantity can a field have depend on total quantity
          const maxQuantity =
            stoItem.quantity -
            stoItem.receive_actual.reduce((sum, pick) => {
              if (
                pick.bin === clonedReceived[idx].bin &&
                pick.inventory_number === clonedReceived[idx].inventory_number
              ) {
                return sum;
              } else {
                return sum + pick.quantity;
              }
            }, 0);
          const fieldQuantity =
            cleanNewValue && +cleanNewValue > 0
              ? Math.floor(+cleanNewValue)
              : 0;
          clonedReceived[idx].quantity =
            fieldQuantity > maxQuantity ? maxQuantity : fieldQuantity;
        } else if (
          type === 'quantity' &&
          (cleanNewValue === '' || Number.isNaN(+cleanNewValue))
        ) {
          clonedReceived[idx].quantity = 0;
        }

        // update state
        setCurrentReceiveActual(clonedReceived);

        /* debounce after state assignment to prevent race condition */
        const {
          bin,
          inventory_number: inventoryNumber,
          quantity,
          basket,
        } = clonedReceived[idx];
        if (type === 'bin' && bin) {
          debouncedProductDetail({
            binNumber: bin,
            pickIndex: idx,
          });
        }
        if (bin && inventoryNumber && quantity && type !== 'basket') {
          debouncedReceiveItem({
            skuCode: stoItem.sku_code,
            stoNumber: stoItem.sto_number,
            item: clonedReceived[idx],
          });
        } else if (
          quantity === 0 &&
          bin !== '' &&
          inventoryNumber !== '' &&
          type === 'quantity'
        ) {
          debouncedRemoveBatch({
            stoNumber: stoItem.sto_number,
            skuCode: stoItem.sku_code,
            item: clonedReceived[idx],
          });
        }

        if (
          nextField &&
          (value.endsWith('\t') ||
            value.endsWith('\n') ||
            (!prevValue && value.length > 1))
        ) {
          debouncedFocusNextField(
            `input[id="field-sto-pick${nextField}-${basket}-${idx}"]`,
          );
        }
      };

      const clearField = (field: keyof STOProcessedItem) => {
        const clonedReceived = structuredClone(currentReceiveActual);
        if (
          field === 'bin' ||
          field === 'inventory_number' ||
          field === 'expiry_date'
        ) {
          // remove batch
          if (
            clonedReceived[idx].bin !== '' &&
            clonedReceived[idx].inventory_number !== ''
          ) {
            const replaced = structuredClone(clonedReceived[idx]);
            debouncedRemoveBatch({
              stoNumber: stoItem.sto_number,
              skuCode: stoItem.sku_code,
              item: replaced,
            });
          }
          clonedReceived[idx][field] = '';
          clonedReceived[idx].quantity = 0;
          clonedReceived[idx].bin_type =
            field === 'bin' ? '' : clonedReceived[idx].bin_type;
        } else if (field === 'is_verified') {
          clonedReceived[idx][field] = false;
        } else if (field === 'quantity') {
          if (
            clonedReceived[idx].bin !== '' &&
            clonedReceived[idx].inventory_number !== ''
          ) {
            debouncedRemoveBatch({
              item: clonedReceived[idx],
              stoNumber: stoItem.sto_number,
              skuCode: stoItem.sku_code,
            });
          }
          clonedReceived[idx][field] = 0;
        }

        setCurrentReceiveActual(clonedReceived);
      };

      return (
        <Grid container style={{ padding: '8px 0' }}>
          <Grid item xs={1} />
          <Grid item xs={11}>
            <Grid container>
              <Grid item xs={5}>
                <p>Bin</p>
              </Grid>
              <S.ReceiveProcessInputGrid item xs={6}>
                <TextField
                  variant="outlined"
                  placeholder="-"
                  value={info.bin}
                  onChange={(event) =>
                    onFieldChange('bin', event.target.value, 'inventory_number')
                  }
                  id={`field-sto-pickbin-${info.basket}-${idx}`}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => clearField('bin')}>
                          <Clear style={{ color: colors.PRIMARY_GRAY }} />
                        </IconButton>
                      </InputAdornment>
                    ),
                    sx: { paddingRight: 0 },
                  }}
                />
              </S.ReceiveProcessInputGrid>
              <Grid item xs={5}>
                <p>Batch Number</p>
              </Grid>
              <S.ReceiveProcessInputGrid item xs={6}>
                <TextField
                  variant="outlined"
                  placeholder="-"
                  value={info.inventory_number}
                  onChange={(event) =>
                    onFieldChange(
                      'inventory_number',
                      event.target.value,
                      'quantity',
                    )
                  }
                  id={`field-sto-pickbatch-${info.basket}-${idx}`}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => clearField('inventory_number')}
                        >
                          <Clear style={{ color: colors.PRIMARY_GRAY }} />
                        </IconButton>
                      </InputAdornment>
                    ),
                    sx: { paddingRight: 0 },
                  }}
                />
              </S.ReceiveProcessInputGrid>
              <Grid item xs={5}>
                <p>Bin Type</p>
              </Grid>
              <S.ReceiveProcessInputGrid item xs={6}>
                <TextField
                  variant="outlined"
                  defaultValue={null}
                  value={info.bin_type || '-'}
                  disabled
                  id={`field-sto-bintype-${info.bin_type}-${idx}`}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton disabled>
                          <Clear style={{ color: '#fff' }} />
                        </IconButton>
                      </InputAdornment>
                    ),
                    sx: { paddingRight: 0 },
                  }}
                />
              </S.ReceiveProcessInputGrid>
              <Grid item xs={5}>
                <p>Expiry Date</p>
              </Grid>
              <S.ReceiveProcessInputGrid item xs={6}>
                <DateField
                  variant="outlined"
                  format="dd/MM/yyyy"
                  defaultValue={null}
                  value={
                    info.expiry_date ? DateTime.fromISO(info.expiry_date) : null
                  }
                  disabled
                  id={`field-sto-receive-expiry-${idx}`}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton disabled>
                          <Clear style={{ color: '#fff' }} />
                        </IconButton>
                      </InputAdornment>
                    ),
                    sx: { paddingRight: 0 },
                  }}
                />
              </S.ReceiveProcessInputGrid>
              <Grid item xs={5}>
                <p>Quantity Picked</p>
              </Grid>
              <S.ReceiveProcessInputGrid item xs={6}>
                <TextField
                  variant="outlined"
                  placeholder="0"
                  type="number"
                  value={info.quantity || ''}
                  onChange={(event) =>
                    onFieldChange('quantity', event.target.value, 'basket')
                  }
                  id={`field-sto-receivequantity-${info.basket}-${idx}`}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => clearField('quantity')}>
                          <Clear style={{ color: colors.PRIMARY_GRAY }} />
                        </IconButton>
                      </InputAdornment>
                    ),
                    sx: { paddingRight: 0 },
                  }}
                />
              </S.ReceiveProcessInputGrid>
            </Grid>
          </Grid>
        </Grid>
      );
    });

  return (
    <>
      {renderForm()}
      <Grid container style={{ marginTop: 8 }}>
        <Grid item xs={6} style={{ textAlign: 'center' }}>
          <S.RedButtonText
            onClick={handleRemoveBatch}
            disabled={currentReceiveActual.length === 1}
            data-testid="BtnSTTaskSourceRemoveBatch"
          >
            REMOVE BATCH
          </S.RedButtonText>
        </Grid>
        <Grid
          item
          xs={6}
          style={{ textAlign: 'right' }}
          data-testid="BtnSTTaskSourceAddBatch"
        >
          <S.BlueButtonText onClick={handleAddBatch}>
            <Add /> ADD BATCH
          </S.BlueButtonText>
        </Grid>
      </Grid>
    </>
  );
};
